import { useContext, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { CollectionContext } from '../../state/CollectionContext'
import style from './comments.module.css'
import globalStyle from '../../styles/common.module.css'
import { findSelectedChunk } from '../../state/util'

export function ReviewComments() {
  const context = useContext(CollectionContext)
  const story = context.state.find((story) => story.isActive)

  const commentsRef = useRef<HTMLDivElement>(null)
  const currentChunk = findSelectedChunk(story) 

  const chunksWithComments = useMemo(() => {
    return story?.content
      .flat()
      .filter((chunk) => chunk.userComment || chunk.voxComment) ?? []
  }, [story])

  const selectedIndex = chunksWithComments.findIndex((chunk) => chunk.id === currentChunk?.id)

  const changeComment = useCallback((dir: number) => {
    if (chunksWithComments.length === 0) return
    let newIndex = selectedIndex + dir
    newIndex = newIndex < 0 ? chunksWithComments.length - 1 : newIndex % chunksWithComments.length
    context.dispatch({ type: 'select_chunk', chunkId: chunksWithComments[newIndex].id })
  }, [selectedIndex, chunksWithComments, context])

  useLayoutEffect(() => {
    if (commentsRef.current) {
      setTimeout(() => {
        commentsRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 300)
    }
  }, [currentChunk, commentsRef])

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        changeComment(-1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        changeComment(1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [changeComment])

  if (!story) return null

  const userCommentCount = chunksWithComments.filter((chunk) => chunk.userComment).length
  const voxCommentCount = chunksWithComments.filter((chunk) => chunk.voxComment).length

  const currentUserComment = currentChunk?.userComment ?? null
  const currentVoxComment = currentChunk?.voxComment ?? null

  if (!currentChunk) return null

  return (
    <div className={[style.comments, globalStyle.grid3].join(' ')} ref={commentsRef}>
      <div className={style.commentsCounts}>
        <div className={style.commentsCount}>
          <p className={[globalStyle.subtitle, globalStyle.iconText, style.countText].join(' ')}>
            <img className={style.countImg} src='assets/icons/editing.svg' alt='icon' />
            Your Edits: {userCommentCount}
          </p>
        </div>
        <div className={style.commentsCount}>
          <p className={[globalStyle.subtitle, globalStyle.iconText, style.countText].join(' ')}>
            <img className={style.countImg} src='assets/icons/lp-logo.svg' alt='icon' />
            LP's Edits: {voxCommentCount}
          </p>
        </div>
      </div>

      <div className={style.comment}>
        <p className={[style.selection, globalStyle.subtitle].join(' ')}>
          &ldquo;{currentVoxComment?.title || currentChunk.text}&rdquo;
        </p>

        {currentUserComment && (
          <div className={style.wrapper}>
            <p
              className={[
                globalStyle.subtitle,
                globalStyle.iconText,
                globalStyle.fontGray2,
                style.commentType,
              ].join(' ')}
            >
              <img src='assets/icons/editing.svg' alt='icon' />
              your edit
            </p>
            <p className={[globalStyle.bodySM, style.commentBody].join(' ')}>
              {' '}
              {currentUserComment}
            </p>
          </div>
        )}

        <div className={style.wrapper}>
          <p
            className={[
              globalStyle.subtitle,
              globalStyle.iconText,
              globalStyle.fontGray2,
              style.commentType,
            ].join(' ')}
          >
            <img src='assets/icons/lp-logo.svg' alt='icon' />
            lp's edit
          </p>
          {currentVoxComment ? (
            <div
              className={[globalStyle.bodySM, style.commentBody].join(' ')}
              dangerouslySetInnerHTML={{
                __html: currentVoxComment.body.html,
              }}
            />
          ) : (
            <p className={[globalStyle.bodySM, style.commentBody].join(' ')}>
              Interesting! We didn’t see an issue with this text. If you think we’re missing
              something fundamental,&nbsp;
              <a href='http://languageplease.org/contact' target='_blank' rel='noopener noreferrer'>
                let us know
              </a>
              .
            </p>
          )}
          {currentVoxComment && currentVoxComment.links.length > 0 && (
            <div>
              {currentVoxComment.links.map((entry) => {
                return (
                  <a
                    className={[
                      globalStyle.button,
                      globalStyle.light,
                      globalStyle.narrow,
                      globalStyle.buttonSM,
                      style.entryLink,
                    ].join(' ')}
                    href={entry.url}
                    key={entry.title}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    See the '{entry.title}' entry
                  </a>
                )
              })}
            </div>
          )}
        </div>

        <div className={style.wrapper}>
          <div className={style.nav}>
            <button className={style.navButton} onClick={() => changeComment(-1)}>
              <img src='assets/icons/arrow2.svg' alt='previous' />
            </button>
            <span className={globalStyle.caption}>
              {selectedIndex + 1} of {chunksWithComments.length}
            </span>
            <button
              className={[style.navButton, style.right].join(' ')}
              onClick={() => changeComment(1)}
            >
              <img src='assets/icons/arrow2.svg' alt='next' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
